import { StylesConfig, GroupBase } from 'react-select';

export type ModernSelectSize = 'compact' | 'regular';

interface SizePreset {
  controlHeight: number;
  fontSize: number;
  horizontalPadding: number;
}

const SIZE_PRESETS: Record<ModernSelectSize, SizePreset> = {
  compact: { controlHeight: 28, fontSize: 11, horizontalPadding: 8 },
  regular: { controlHeight: 36, fontSize: 13, horizontalPadding: 12 },
};

export const buildModernSelectStyles = <TOption, IsMulti extends boolean>(
  primary: string,
  size: ModernSelectSize
): StylesConfig<TOption, IsMulti, GroupBase<TOption>> => {
  const preset: SizePreset = SIZE_PRESETS[size];
  const innerHeight: number = preset.controlHeight - 2;

  return {
    container: (provided) => ({
      ...provided,
      minWidth: '200px',
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: `${preset.controlHeight}px`,
      height: `${preset.controlHeight}px`,
      backgroundColor: state.isDisabled ? '#f3f4f6' : 'transparent',
      border: `1px solid ${state.isDisabled ? '#d1d5db' : primary}`,
      borderRadius: '4px',
      boxShadow: state.isFocused ? `0 0 0 1px ${primary}` : 'none',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      fontFamily: 'inherit',
      opacity: state.isDisabled ? 0.7 : 1,
      '&:hover': {
        borderColor: state.isDisabled ? '#d1d5db' : primary,
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: `0 ${preset.horizontalPadding}px`,
      height: `${innerHeight}px`,
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? '#9ca3af' : primary,
      fontFamily: 'inherit',
      fontWeight: 700,
      fontSize: `${preset.fontSize}px`,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? '#9ca3af' : primary,
      fontFamily: 'inherit',
      fontWeight: 700,
      fontSize: `${preset.fontSize}px`,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      color: primary,
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: `${innerHeight}px`,
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? '#9ca3af' : primary,
      padding: '0 4px',
      '&:hover': {
        color: state.isDisabled ? '#9ca3af' : primary,
      },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    menu: (provided) => ({
      ...provided,
      fontSize: '12px',
      fontWeight: 600,
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    option: (provided, state) => ({
      ...provided,
      cursor: 'pointer',
      color: state.isSelected ? '#ffffff' : '#1f2937',
      backgroundColor: state.isSelected
        ? primary
        : state.isFocused
          ? `${primary}1a`
          : 'transparent',
    }),
  };
};

export const mergeStyles = <TOption, IsMulti extends boolean>(
  base: StylesConfig<TOption, IsMulti, GroupBase<TOption>>,
  override?: StylesConfig<TOption, IsMulti, GroupBase<TOption>>
): StylesConfig<TOption, IsMulti, GroupBase<TOption>> => {
  if (!override) {
    return base;
  }

  const merged: StylesConfig<TOption, IsMulti, GroupBase<TOption>> = { ...base };
  const overrideKeys = Object.keys(override) as Array<keyof typeof override>;

  for (const key of overrideKeys) {
    const baseFn: any = (base as any)[key];
    const overrideFn: any = (override as any)[key];

    (merged as any)[key] = (provided: any, state: any) => {
      const afterBase = baseFn ? baseFn(provided, state) : provided;
      return overrideFn(afterBase, state);
    };
  }

  return merged;
};
